import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import OfferCalendarMonthPicker from '../CalendarPicker/MonthPicker/OfferCalendarMonthPicker'
import OfferCalendarPointsToggle from '../CalendarPicker/OfferCalendarPointsToggle'
import OfferCalendarHeaderExtensionContainer from '../CalendarPicker/OfferCalendarHeaderExtensionContainer'
import OfferCalendarDatePickerFlightDealBanner from '../CalendarPicker/OfferCalendarDatePickerFlightDealBanner'
import { CalendarBurnProvider } from '../CalendarPicker/CalendarBurnContext'
import useCalendarBurnToggleVisible from '../CalendarPicker/useCalendarBurnToggleVisible'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCalendarSidebarDayPicker from './OfferCalendarSidebarDayPicker'
import { getMultiRoomCalendarMonths, getMultiRoomDaysOtherAvailability, getSelectedOfferPageMultiRoomDay, isMultiRoomFetchingCalendars } from 'selectors/offerPage/multipleRoomBookingSelectors'
import OfferCalendarNotAvailableMessage from '../CalendarPicker/OfferCalendarNotAvailableMessage'
import OfferCalendarNoFlightsMessage from '../CalendarPicker/OfferCalendarNoFlightsMessage'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import OfferCalendarRoomSelect from '../CalendarPicker/CalendarRooms/OfferCalendarRoomSelect'
import OfferCalendarDurationSelect from '../CalendarPicker/CalendarDuration/OfferCalendarDurationSelect'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import Group from 'components/utils/Group'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import OfferCalendarFlightsToggle from '../CalendarPicker/OfferCalendarFlightsToggle'
import noop from 'lib/function/noop'
import OfferCalendarCapacitySelect from '../CalendarPicker/CalendarCapacity/OfferCalendarCapacitySelect'
import { nonNullable } from 'lib/array/arrayUtils'
import { isFlashHotelOffer, isVillaOffer } from 'lib/offer/offerTypes'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import OfferCalendarLuxPlusToggle from 'luxPlus/components/OfferCalendarLuxPlusToggle'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferCalendarSidebarDatePickerFooter from './OfferCalendarSidebarDatePickerFooter'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import OfferCalendarAirportSelect from '../CalendarPicker/CalendarAirport/OfferCalendarAirportSelect'
import OfferCalendarRoomLineSelect from '../CalendarPicker/CalendarRooms/OfferCalendarRoomLineSelect'
import * as Analytics from 'analytics/analytics'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getCalendarMonths, getDaysOtherAvailability, getSelectedOfferPageDay, isFetchingCalendars, getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import styled from 'styled-components'
import { rem } from 'polished'
import Caption from 'LuxKit/Typography/Caption'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import OfferCalendarConfirmDatesFooter from './OfferCalendarConfirmDatesFooter'
import useFixedFlashLengthOfStayExperiment from '../CalendarPicker/useFixedFlashLengthOfStayExperiment'
import { shouldClearUnavailableCheckIn } from '../CalendarPicker/fixedFlashLengthOfStayCalendarUtils'
import { getFirstAvailableDuration } from 'components/OfferPage/FlashHotel/flashHotelCalendarSkipUtils'

const DaysHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`

const CalendarBodyWrapper = styled.div`
  &.with-burn-toggle {
    padding-block: ${rem(20)};
  }
`

interface Props {
  currentOfferId: string
  offer: App.Offer
  airport?: App.Airport
  fetching?: boolean
  checkIn?: string
  checkOut?: string
  selectedDay?: App.CalendarDay
  calendarError?: boolean
  calendarAvailability: boolean
  defaultDayPicker?: boolean
  defaultMonth?: string
  onBack?: () => void
  onClose?: () => void
  subFilters?: Array<OfferPageFilterType>
  isLuxPlusEnabled: boolean
  room?: App.PackageRoomType
  isAnyFlightsActive: boolean
  useConfirmDatesFooter?: boolean
  isMultiRoom: boolean
  otherAvailability: { [checkIn: string]: boolean }
  isAlternativeDatesFlow: boolean
  minimumDuration?: number
}

const defaultSubfilters: Array<OfferPageFilterType> = ['duration', 'flights-toggle', 'room']
function OfferCalendarSidebarDatePicker(props: Props) {
  const {
    offer,
    fetching,
    selectedDay,
    checkIn,
    checkOut,
    calendarError,
    calendarAvailability,
    defaultMonth,
    onBack,
    onClose = noop,
    currentOfferId,
    subFilters = defaultSubfilters,
    isLuxPlusEnabled,
    airport,
    isAnyFlightsActive,
    useConfirmDatesFooter,
    isMultiRoom,
    otherAvailability,
    minimumDuration,
  } = props

  const intl = useIntl()
  const isBurnToggleVisible = useCalendarBurnToggleVisible()
  const { currentRegionHasFlights } = useContext(GeoContext)
  const modalContext = useModalElementContext()

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const isMobileScreen = useScreenSizeOnly('mobile')
  const useNewMobileHotelOfferPage = isMobileScreen && offer.type === OFFER_TYPE_HOTEL

  const scrollerRef = useRef<HTMLDivElement>(null)
  const [selectedMonth, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()

  const flightsEnabled = currentRegionHasFlights && offer.offerFlightsEnabled && !offer.bundledWithFlightsOnly
  const hasSelectedMonth = selectedMonth !== undefined
  const isFixedFlashLengthOfStayOffer = isFlashHotelOffer(offer)
  // Mobile sidebar activates the experiment as soon as the calendar modal opens. Desktop
  // OfferCalendarDatePicker waits until the check-in filter is open. Snowplow activation
  // funnels therefore differ by platform; bucket reads still align via useExperiment.
  const shouldActivateFixedFlashLengthOfStayExperiment = isFixedFlashLengthOfStayOffer && !props.isAlternativeDatesFlow
  const isFixedFlashLengthOfStayVariant = useFixedFlashLengthOfStayExperiment(shouldActivateFixedFlashLengthOfStayExperiment)
  const isFixedFlashLengthOfStayEnabled = isFixedFlashLengthOfStayOffer && isFixedFlashLengthOfStayVariant
  const calendarSubFilters = isFixedFlashLengthOfStayEnabled
    ? subFilters.filter(filter => filter !== 'duration')
    : subFilters
  const shouldHideContinue = isFixedFlashLengthOfStayEnabled && !!checkIn && !checkOut

  const onBackToMonth = useCallback(() => {
    scrollerRef.current?.scrollTo({ top: 0 })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    if (isFixedFlashLengthOfStayEnabled && minimumDuration !== undefined) {
      dispatch({ type: OfferPageActions.resetDatesToMinimumDuration, minimumDuration })
    } else if (!useNewMobileHotelOfferPage) {
      // Legacy sidebar: clear check-in only; setCheckInDate also clears check-out when duration is set.
      // Desktop OfferCalendarDatePicker uses resetDates instead (see onBackToMonth there).
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    }
    // New mobile hotel sidebar intentionally keeps dates when returning to the month picker.
    setMonth(undefined)
  }, [onUserAnalyticsAction, isFixedFlashLengthOfStayEnabled, minimumDuration, useNewMobileHotelOfferPage, dispatch, currentOfferId])

  const selectedDayOtherAvailability = selectedDay ? otherAvailability[selectedDay.checkIn] : undefined

  useEffect(() => {
    if (shouldClearUnavailableCheckIn({
      isFixedFlashLengthOfStayEnabled,
      checkIn,
      checkOut,
      fetching,
      selectedDay,
      hasOtherAvailability: !!selectedDay && !!selectedDayOtherAvailability,
    })) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    }
  }, [
    checkIn,
    checkOut,
    selectedDay,
    isFixedFlashLengthOfStayEnabled,
    fetching,
    selectedDayOtherAvailability,
    dispatch,
    currentOfferId,
  ])

  const capacities = useMemo(() => nonNullable(offer.packages.flatMap(pkg => pkg.roomRate?.capacities)), [offer])

  const onModalClose = useCallback(() => {
    onClose()
    modalContext.resolve()
  }, [onClose, modalContext])

  const onModalBack = useCallback(() => {
    if (selectedMonth) {
      onBackToMonth()
    } else {
      onBack?.()
    }
  }, [onBack, onBackToMonth, selectedMonth])

  const onMonthSelect = useCallback((monthKey: string) => {
    scrollerRef.current?.scrollTo({ top: 0 })
    setMonth(monthKey)
  }, [])

  const showBack = !!onBack || !!selectedMonth

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: hasSelectedMonth ? 'select_day' : 'select_month',
      action: 'appear',
      type: 'interaction',
      category: 'offer_funnel',
    })
  }, [hasSelectedMonth])

  const modalTitle = intl.formatMessage({
    id: 'SpcABLT_LR1-y1XRPBza',
    defaultMessage: 'Select your dates',
    description: 'Title for sidebar calendar date picker modal in the calendar picker',
  })

  return (<CalendarBurnProvider>
    <ModalHeader
      title={modalTitle}
      onCloseButtonClick={onModalClose}
      onBackButtonClick={showBack ? onModalBack : undefined}
    >
      <Group direction="vertical" gap={20}>
        <HorizontalContainerWithFadedOverflow overflowGutter={20} gap={8}>
          {isLuxPlusEnabled && offer.luxPlus.hasMemberPrices && <OfferCalendarLuxPlusToggle />}
          {calendarSubFilters.includes('duration') && <OfferCalendarDurationSelect variant="modal" />}
          {useNewMobileHotelOfferPage && !isMultiRoom && calendarSubFilters.includes('room') && <OfferCalendarRoomLineSelect variant="modal" />}
          {calendarSubFilters.includes('capacity') && <OfferCalendarCapacitySelect variant="modal" capacities={capacities} disableMultiRoom={isVillaOffer(offer)} />}
          {!useNewMobileHotelOfferPage && calendarSubFilters.includes('room') && <OfferCalendarRoomSelect variant="modal" />}
          {flightsEnabled && calendarSubFilters.includes('flights-toggle') && <OfferCalendarFlightsToggle />}
          {flightsEnabled && isAnyFlightsActive && calendarSubFilters.includes('airport') && <OfferCalendarAirportSelect />}
        </HorizontalContainerWithFadedOverflow>
        {hasSelectedMonth && !fetching && (
          <DaysHeader>
            {/* this might break in languages with different first day of the week/different date abbreviations */}
            <Caption align="center" colour="light-foreground-alt" variant="large" as="abbr" title={intl.formatMessage({ id: 'JX6mb66w9JK57pIirHjU', defaultMessage: 'Monday', description: 'Full day name for Monday in calendar header in the calendar picker' })}>{intl.formatMessage({ id: 'MYq2j2H2Ah6fdXxnFfIe', defaultMessage: 'Mon', description: 'Abbreviated day name for Monday in calendar header' })}</Caption>
            <Caption align="center" colour="light-foreground-alt" variant="large" as="abbr" title={intl.formatMessage({ id: 'vzGU3HKawME12pOLu0Sd', defaultMessage: 'Tuesday', description: 'Full day name for Tuesday in calendar header in the calendar picker' })}>{intl.formatMessage({ id: '3wUKXX9ArW3H3EBoCGJA', defaultMessage: 'Tue', description: 'Abbreviated day name for Tuesday in calendar header' })}</Caption>
            <Caption align="center" colour="light-foreground-alt" variant="large" as="abbr" title={intl.formatMessage({ id: 'Y4VtQz5hiJNAXfEyzLeh', defaultMessage: 'Wednesday', description: 'Full day name for Wednesday in the calendar header' })}>{intl.formatMessage({ id: 'o8pdOSQJkZoNg5HKIUjb', defaultMessage: 'Wed', description: 'Abbreviated day name for Wednesday in calendar header' })}</Caption>
            <Caption align="center" colour="light-foreground-alt" variant="large" as="abbr" title={intl.formatMessage({ id: 'gOOedbdh9oGgDpoytoP-', defaultMessage: 'Thursday', description: 'Full day name for Thursday in the calendar header on the offer detail page' })}>{intl.formatMessage({ id: 'j8tASNhaMksnI_tSqknd', defaultMessage: 'Thu', description: 'Abbreviated day name for Thursday in calendar header' })}</Caption>
            <Caption align="center" colour="light-foreground-alt" variant="large" as="abbr" title={intl.formatMessage({ id: 'kGb1qRpQo0WHVcovNwG8', defaultMessage: 'Friday', description: 'Full day name for Friday in calendar header in the calendar picker' })}>{intl.formatMessage({ id: 'woBz4_hEkmup92OBGWYj', defaultMessage: 'Fri', description: 'Abbreviated day name for Friday in calendar header' })}</Caption>
            <Caption align="center" colour="light-foreground-alt" variant="large" as="abbr" title={intl.formatMessage({ id: 'n7ya1fna8_s6hb5EHQBg', defaultMessage: 'Saturday', description: 'Full day name for Saturday in the calendar header on the offer detail page' })}>{intl.formatMessage({ id: 'HaMvQSO8j-DcpLAK53gN', defaultMessage: 'Sat', description: 'Abbreviated day name for Saturday in calendar header' })}</Caption>
            <Caption align="center" colour="light-foreground-alt" variant="large" as="abbr" title={intl.formatMessage({ id: 'wG2aSI7MddhdEawRA54T', defaultMessage: 'Sunday', description: 'Full day name for Sunday in calendar header in the calendar picker' })}>{intl.formatMessage({ id: 'pflpryaNH_SvX8zItYvg', defaultMessage: 'Sun', description: 'Abbreviated day name for Sunday in calendar header' })}</Caption>
          </DaysHeader>
        )}
      </Group>
      <OfferCalendarHeaderExtensionContainer>
        <OfferCalendarPointsToggle />
        <OfferCalendarDatePickerFlightDealBanner
          offer={offer}
          airport={airport}
          isFlightsActive={isAnyFlightsActive}
        />
      </OfferCalendarHeaderExtensionContainer>
    </ModalHeader>
    <ModalBody ref={scrollerRef}>
      <ModalContent>
        <CalendarBodyWrapper className={isBurnToggleVisible ? 'with-burn-toggle' : undefined}>
          <LoadingIndicator floating visible={!!(fetching && hasSelectedMonth)} />
          {!fetching && (calendarError || !isAnyFlightsActive) && !calendarAvailability && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
          {!fetching && !calendarError && isAnyFlightsActive && !calendarAvailability && <OfferCalendarNoFlightsMessage canSwitchToHotel={!offer?.bundledWithFlightsOnly} />}

          {calendarAvailability && <VerticalSpacer gap={8}>
            {hasSelectedMonth && !fetching && <OfferCalendarSidebarDayPicker
              onBackToMonth={onBackToMonth}
              initialMonth={selectedMonth}
              flightsOnly={offer.bundledWithFlightsOnly}
              isFixedFlashLengthOfStayEnabled={isFixedFlashLengthOfStayEnabled}
              contentRef={scrollerRef}
            />}

            {!hasSelectedMonth && <OfferCalendarMonthPicker
              onMonthSelect={onMonthSelect}
            />}
          </VerticalSpacer>}
        </CalendarBodyWrapper>
      </ModalContent>
    </ModalBody>
    {!useConfirmDatesFooter && <OfferCalendarSidebarDatePickerFooter
      hideContinue={shouldHideContinue}
      isFixedFlashLengthOfStayEnabled={isFixedFlashLengthOfStayEnabled}
    />}
    {useConfirmDatesFooter && <OfferCalendarConfirmDatesFooter
      onApply={onModalClose}
      isFixedFlashLengthOfStayEnabled={isFixedFlashLengthOfStayEnabled}
    />}
  </CalendarBurnProvider>)
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  return {
    currentOfferId: state.paginator?.currentOfferId,
    airport: state.airport,
    calendarAvailability: isMultiRoom ? getMultiRoomCalendarMonths(state, appState).availableCount > 0 : getCalendarMonths(appState, state).availableCount > 0,
    otherAvailability: isMultiRoom ? getMultiRoomDaysOtherAvailability(state, appState) : getDaysOtherAvailability(appState, state),
    calendarError: state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    fetching: isMultiRoom ? isMultiRoomFetchingCalendars(state, appState) : isFetchingCalendars(appState, state),
    offer: state.offer,
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    isLuxPlusEnabled: getIsLuxPlusEnabled(appState),
    isAnyFlightsActive: state.flights,
    isMultiRoom,
    isAlternativeDatesFlow: !!state.isAlternativeDatesFlow,
    minimumDuration: getFirstAvailableDuration(getDurationOptions(state, appState)),
  }
}, OfferCalendarSidebarDatePicker)
